import { FC, useState, useEffect, useMemo, useRef } from 'react';
import {
  TextField,
  Popper,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  ClickAwayListener,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { useSelector, RootState } from '../../store';
import { useTranslation } from 'react-i18next';
import { projectGetMembers } from '../../api/project';
import { documentsGetAll } from '../../slices/documentSlice';
import { TAB_REGISTRY, FIELD_REGISTRY } from '../../config/field-registry';
import { buildFieldKey, buildTabKey } from '../../utils/field-key';

interface Suggestion {
  type: string;
  id: string;
  label: string;
}

interface MentionInputProps {
  value: string;
  mentions: any[];
  onChange: (text: string, mentions: any[]) => void;
  placeholder?: string;
  rows?: number;
  /** Focus the composer on mount/open (e.g. when the comments drawer opens). */
  autoFocus?: boolean;
}

// Strip token-delimiter chars from a label so the inline @[label](type:id) token
// stays parseable.
const sanitize = (s: string) => (s || '').replace(/[\]()]/g, '');

// A composer with @-mention autocomplete. Typing "@" opens a Popper suggesting
// project members, reviews, fields and documents. Selecting one inserts an
// inline token and records the structured mention.
export const MentionInput: FC<MentionInputProps> = ({ value, mentions, onChange, placeholder, rows = 3, autoFocus = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const inputRef = useRef<any>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState<string | null>(null);
  const [atPos, setAtPos] = useState<number>(-1);
  const [members, setMembers] = useState<any[]>([]);
  const documents = useSelector((s: RootState) => s.document.documents);

  useEffect(() => {
    (async () => {
      const res = await projectGetMembers();
      if (Array.isArray(res)) setMembers(res);
    })();
    dispatch(documentsGetAll() as any);
  }, [dispatch]);

  const suggestions = useMemo<Suggestion[]>(() => {
    const m = (members || []).map((x: any) => ({ type: 'member', id: String(x.id), label: x.name || x.email || 'member' }));
    const reviews = TAB_REGISTRY.map((tdef) => ({ type: 'review', id: buildTabKey(tdef.review, tdef.screen), label: t(tdef.label) }));
    const fields = FIELD_REGISTRY.map((f) => ({ type: 'field', id: buildFieldKey(f.review, f.screen, f.field), label: t(f.label) }));
    const docs = (documents || []).map((d: any) => ({ type: 'document', id: String(d.id), label: d.title }));
    return [...m, ...reviews, ...fields, ...docs];
  }, [members, documents, t]);

  const filtered = useMemo(() => {
    if (query === null) return [];
    const q = query.toLowerCase();
    return suggestions.filter((s) => (s.label || '').toLowerCase().includes(q)).slice(0, 8);
  }, [query, suggestions]);

  const handleChange = (e: any) => {
    const text = e.target.value as string;
    const caret = (e.target.selectionStart as number) ?? text.length;
    onChange(text, mentions);
    const before = text.slice(0, caret);
    const at = before.lastIndexOf('@');
    if (at >= 0) {
      const token = before.slice(at + 1);
      if (!/\s/.test(token)) {
        setAtPos(at);
        setQuery(token);
        setAnchorEl(inputRef.current);
        return;
      }
    }
    setQuery(null);
    setAtPos(-1);
  };

  const select = (s: Suggestion) => {
    const caret = (inputRef.current && inputRef.current.selectionStart) ?? value.length;
    const token = `@[${sanitize(s.label)}](${s.type}:${s.id})`;
    const newText = value.slice(0, atPos) + token + ' ' + value.slice(caret);
    const exists = mentions.some((mm) => mm.type === s.type && mm.targetId === s.id);
    const newMentions = exists ? mentions : [...mentions, { type: s.type, targetId: s.id, label: sanitize(s.label) }];
    onChange(newText, newMentions);
    setQuery(null);
    setAtPos(-1);
  };

  return (
    <>
      <TextField
        inputRef={inputRef}
        autoFocus={autoFocus}
        fullWidth
        multiline
        rows={rows}
        size="small"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
      />
      <Popper
        open={query !== null && filtered.length > 0}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: 1500 }}
      >
        <ClickAwayListener onClickAway={() => setQuery(null)}>
          <Paper sx={{ width: 300, maxHeight: 240, overflowY: 'auto', boxShadow: 6 }}>
            <List dense>
              {filtered.map((s) => (
                <ListItem button key={`${s.type}:${s.id}`} onClick={() => select(s)}>
                  <Chip size="small" label={s.type} sx={{ mr: 1 }} />
                  <ListItemText primary={s.label} primaryTypographyProps={{ noWrap: true }} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
